import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createIndustry, fetchIndustries } from "../services/industries.service"
import { useEffect, useMemo, useRef, useState } from "react"
import { NumberParam, StringParam, useQueryParam } from "use-query-params"
import Swal from "sweetalert2"
import { useAuth } from "../../../context/auth-user"

export const BATCH_SIZE = 8

export const useIndustriesTable = () => {
    const { user } = useAuth()
    const userLoggedRol = user?.selectedProfile?.rol
    const userLoggedDataID = user?.selectedProfile?.dataID
    
    const queryClient = useQueryClient()
    
    const [currentPage, setCurrentPage] = useQueryParam("page", NumberParam)
    
    const [searchValue, setSearchValue] = useState("")
    const [searchFilter, setSearchFilter] = useQueryParam("industries-search", StringParam)
    
    const [sortField, setSortField] = useQueryParam("industries-sort-field", StringParam)
    const [sortOrder, setSortOrder] = useQueryParam("industries-sort-order", StringParam)
    
    const activeSort = useMemo(() => ({
        field: sortField || "name",
        order: sortOrder || "ASC",
    }), [sortField, sortOrder])
    
    const setActiveSort = (newSort) => {
        setSortField(newSort.field)
        setSortOrder(newSort.order)
    }

    const [showSortMenu, setShowSortMenu] = useState(false)
    const [showAddModal, setShowAddModal] = useState(false)

    const [showIndustryOptionsMenu, setShowIndustryOptionsMenu] = useState(false)
        
    const sortMenuRef = useRef(null)
    const menuOptionsRef = useRef(null)

    const industriesQueryKey = ["industries-table", searchFilter, activeSort, currentPage ?? 1, userLoggedRol, userLoggedDataID]
    
    // Fetch industries
    const {
        data,
        isLoading,
        isFetching,
        isError,
        refetch,
    } = useQuery({
        queryKey: industriesQueryKey,
        queryFn: () => 
            fetchIndustries({ 
                page: currentPage ?? 1,
                limit: BATCH_SIZE,
                sort: activeSort,
                search: searchFilter || "",
                rol: userLoggedRol,
                dataID: userLoggedDataID,
            }),
        staleTime: 1000 * 60 * 5,
        retry: 2,
        refetchOnMount: true,
        enabled: !!userLoggedRol && !!userLoggedDataID,
        placeholderData: keepPreviousData,
    })
    
    // Create industry mutation
    const createIndustryMutation = useMutation({
        mutationFn: createIndustry,
        onMutate: () => {
            Swal.fire({
                footer: 'Loading, please wait',
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading()
                },
            })
        },
        onSuccess: () => {
            Swal.fire({
                icon: 'success',
                text: 'Information saved successfully',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
            
            setShowAddModal(false)
            queryClient.invalidateQueries({ queryKey: ["industries-table"] })
        },
        onError: (error) => {
            Swal.fire({
                icon: 'error',
                text: 'Failed to save information',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            })
            console.error("Error al guardar la industria:", error)
        }
    })
    
    useEffect(() => {
        const closeMenusFilters = (e) => {
            if (!sortMenuRef?.current?.contains(e.target)) {
                setShowSortMenu(false)
            }
            
            if (!menuOptionsRef?.current?.contains(e.target)) {
                setShowIndustryOptionsMenu(false)
            }
        }

        document.body.addEventListener("mousedown", closeMenusFilters)

        return () => {
            document.body.removeEventListener("mousedown", closeMenusFilters)
        } 
    }, [])

    useEffect(() => {
        Swal.fire({
            footer: 'Loading, please wait',
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                Swal.showLoading()
            },
        })

        if (!isLoading) {
            Swal.close()
        }
    }, [isLoading])
    

    const handlePageChange = (page) => {
        if (page <= 1) return setCurrentPage(undefined)
        
        setCurrentPage(page)
    }

    const handleShowSortMenu = () => {
        setShowSortMenu(prev => !prev)
    }

    const handleSortChange = (fieldName, order) => {
        setCurrentPage(undefined)
        
        let newOrder = order || "ASC"
        
        if (activeSort.field === fieldName && !order) {
            newOrder = activeSort.order === "ASC" ? "DSC" : "ASC"
        }

        setActiveSort({
            field: fieldName,
            order: newOrder,
        })
    }

    const handleSearchChange = (e) => {
        const value = e.target.value.trim()
        setSearchValue(value)
    }

    const handleSetSearchFilter = (e) => {
        e.preventDefault()
        setCurrentPage(undefined)
        setSearchFilter(searchValue || undefined)
    }
    
    const handleShowAddModal = () => {
        setShowAddModal(true)
    }
    
    const handleHideAddModal = () => {
        setShowAddModal(false)
    }

    const handleShowIndustryOptionsMenu = (e,industryName) => {
        if (showIndustryOptionsMenu === industryName) {
            setShowIndustryOptionsMenu(false)
            return
        }
        
        menuOptionsRef.current = e.target
        setShowIndustryOptionsMenu(industryName)
    }
    
    const handleCreateIndustry = (industryName) => {
        createIndustryMutation.mutate(industryName)
    }
    
    const allIndustries = data?.industries || []
    const totalPages = data?.totalPages || 0
    const totalCount = data?.totalCount || 0

    return {
        data: allIndustries,
        isLoading,
        isFetching,
        isError,
        refetch,
        currentPage,
        totalPages,
        totalCount,
        handlePageChange,
        
        handleShowSortMenu,
        showSortMenu,
        handleSortChange,
        
        searchValue,
        handleSearchChange,
        handleSetSearchFilter,

        sortMenuRef,
        showIndustryOptionsMenu,
        handleShowIndustryOptionsMenu,
        menuOptionsRef,
        
        showAddModal,
        handleShowAddModal,
        handleHideAddModal,
        handleCreateIndustry,
    }
}
